import { useCallback, useMemo, useState } from "react";

import type { LocationChipItem } from "app/pages/sales-intelligence/common-components/dropdown/LocationDropdown/types";
import { LimitedChipItems, type ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterItemCard } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemCard/FilterItemCard";
import { FilterItemClearButton } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemClearButton/FilterItemClearButton";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { MAX_CHIPS_GROUP_AMOUNT } from "app/pages/sales-intelligence/common-components/filters-panel/constants";
import type { IEmployeeLocationFilterDto } from "app/pages/sales-intelligence/domain/contacts/types/filter-values";
import { assembleEmployeeLocationFilterDTO } from "app/pages/sales-intelligence/domain/contacts/utils/filter-dto-adapters";
import { usePlaybooksTrackingService } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybooksTrackingService";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { getSelectedCodes } from "app/pages/sales-intelligence/utils/location";
import { buildLocationChips, getCodesAfterLocationChipRemoval } from "app/pages/sales-intelligence/utils/locationChips";
import type { ICountryObject } from "app/services/CountryService";
import type { IRegionObject } from "app/services/RegionService";

import { PlaybookEmployeeLocationSelector } from "./PlaybookEmployeeLocationSelector";

const hasLocationValue = (
    location: IEmployeeLocationFilterDto | undefined,
    regionsList: readonly IRegionObject[],
): boolean => getSelectedCodes(location, regionsList).size > 0;

type PlaybookEmployeeLocationFilterCardProps = {
    value?: IEmployeeLocationFilterDto;
    onChange: (value?: IEmployeeLocationFilterDto) => void;
    countries: readonly ICountryObject[];
    regions: readonly IRegionObject[];
};

export const PlaybookEmployeeLocationFilterCard = ({
    value,
    onChange,
    countries,
    regions,
}: PlaybookEmployeeLocationFilterCardProps) => {
    const hasValue = hasLocationValue(value, regions);
    const [isExpanded, setIsExpanded] = useState(!hasValue);
    const playbooksTracking = usePlaybooksTrackingService();

    const chips = useMemo(() => buildLocationChips({ value, regions, countries }), [value, regions, countries]);

    const handleValueChange = useCallback(
        (nextValue?: IEmployeeLocationFilterDto) => {
            playbooksTracking.trackEmployeeLocationChange();
            onChange(nextValue);
        },
        [onChange, playbooksTracking],
    );

    const handleClear = useCallback(() => {
        playbooksTracking.trackEmployeeLocationClear();
        onChange(undefined);
    }, [onChange, playbooksTracking]);

    const handleChipDelete = useCallback(
        (itemId: string, item: ChipItem) => {
            if (!value) {
                return;
            }

            const inclusion = value.inclusion ?? inclusionOptionMap.includeOnly;
            const newCodes = getCodesAfterLocationChipRemoval(
                item as LocationChipItem,
                itemId,
                getSelectedCodes(value, regions),
            );

            handleValueChange(assembleEmployeeLocationFilterDTO(inclusion, newCodes, []));
        },
        [handleValueChange, regions, value],
    );

    const handleToggleExpand = useCallback(() => {
        setIsExpanded((current) => !current);
    }, []);

    const filterResultView =
        chips.length > 0 ? (
            <FilterResultContainer data-automation="playbook-wizard-employee-location-chips">
                <LimitedChipItems
                    items={chips}
                    maxItems={MAX_CHIPS_GROUP_AMOUNT}
                    hideActionButton={false}
                    isTabExpanded={isExpanded}
                    onDelete={handleChipDelete}
                />
            </FilterResultContainer>
        ) : null;

    return (
        <FilterItemCard
            dataAutomation="playbook-wizard-employee-location-filter"
            title={{ key: "si.lead_gen_filters.employee_location" }}
            tooltipText={{ key: "si.lead_gen_filters.category.employee_location.tooltip" }}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            filterResultView={filterResultView}
            renderActions={() => (hasValue ? <FilterItemClearButton onClick={handleClear} /> : null)}
        >
            <PlaybookEmployeeLocationSelector
                value={value}
                onChange={handleValueChange}
                countries={countries}
                regions={regions}
            />
        </FilterItemCard>
    );
};
